// Site-wide search index with deep clinical + colloquial keywords.
// Patients can type any skin concern and land on the right page.

export interface SearchItem {
  title: string;
  description: string;
  route: string;
  category: "Treatment" | "Condition" | "Technology" | "Service" | "Info";
  keywords: string[];
}

export const SEARCH_INDEX: SearchItem[] = [
  {
    title: "Acne & Breakouts",
    description: "Clear active acne, cysts, and prevent future breakouts",
    route: "/acne-breakouts",
    category: "Condition",
    keywords: ["acne", "pimples", "breakouts", "cystic acne", "hormonal acne", "blackheads", "whiteheads", "congested skin", "oily skin", "blemishes", "spots", "zits", "adult acne", "back acne", "chin acne", "jawline acne", "forehead pimples", "clogged pores", "comedones", "papules", "pustules", "nodular acne", "sebaceous", "sebum", "inflamed skin", "breakout"],
  },
  {
    title: "Acne Scars",
    description: "Treat ice pick, boxcar, and rolling acne scars",
    route: "/acne-scars",
    category: "Condition",
    keywords: ["acne scars", "acne scarring", "ice pick scars", "boxcar scars", "rolling scars", "atrophic scars", "pitted skin", "pockmarks", "post acne marks", "pih", "post inflammatory hyperpigmentation", "red marks after acne", "dark marks after pimples", "indented scars", "depressed scars", "textured skin from acne", "acne holes", "scarring from breakouts", "old acne marks", "scar"],
  },
  {
    title: "Dark Circles & Under-Eye Hollowing",
    description: "Tear trough filler, PRF, and RF to refresh tired eyes",
    route: "/dark-circles",
    category: "Condition",
    keywords: ["dark circles", "under eye", "under-eye hollowing", "tear trough", "eye bags", "puffy eyes", "tired eyes", "hollow eyes", "sunken eyes", "periorbital", "dark under eyes", "shadowy eyes", "eye area aging", "under eye filler", "under eye prf", "under eye prp", "under eye discoloration", "blue circles under eyes", "purple under eyes", "dark pigment under eyes", "under eye wrinkles", "crepey under eyes", "fine lines under eyes", "dark eye"],
  },
  {
    title: "Hyperpigmentation & Dark Spots",
    description: "Fade melasma, sunspots, and uneven skin tone",
    route: "/pigmentation",
    category: "Condition",
    keywords: ["hyperpigmentation", "dark spots", "melasma", "sunspots", "age spots", "liver spots", "brown spots", "uneven skin tone", "blotchy skin", "discoloration", "pih", "post inflammatory hyperpigmentation", "freckles", "sun damage", "sun spots", "chloasma", "pregnancy mask", "hormonal pigmentation", "patchy skin", "uneven complexion", "dark patches", "skin discoloration", "tanning", "tan spots", "lentigines", "solar lentigines", "brown patch", "dark patch"],
  },
  {
    title: "Fine Lines & Wrinkles",
    description: "Smooth crows feet, forehead lines, and expression wrinkles",
    route: "/fine-lines-wrinkles",
    category: "Condition",
    keywords: ["fine lines", "wrinkles", "crows feet", "forehead lines", "forehead wrinkles", "frown lines", "11 lines", "eleven lines", "glabellar lines", "expression lines", "laugh lines", "smile lines", "nasolabial folds", "marionette lines", "lip lines", "perioral lines", "smoker lines", "neck lines", "neck wrinkles", "tech neck", "bunny lines", "anti-aging", "aging skin", "dynamic wrinkles", "static wrinkles", "deep wrinkles", "creases", "skin creasing", "wrinkle"],
  },
  {
    title: "Enlarged Pores",
    description: "Minimize pore size and refine skin texture",
    route: "/enlarged-pores",
    category: "Condition",
    keywords: ["enlarged pores", "large pores", "big pores", "visible pores", "open pores", "pore size", "pore minimizing", "rough texture", "uneven texture", "bumpy skin", "orange peel skin", "oily pores", "congested pores", "blackhead pores", "strawberry skin", "skin texture issues", "coarse skin", "pore"],
  },
  {
    title: "Redness & Rosacea",
    description: "Calm persistent redness, flushing, and broken capillaries",
    route: "/redness-rosacea",
    category: "Condition",
    keywords: ["redness", "rosacea", "flushing", "blushing", "red face", "broken capillaries", "spider veins on face", "telangiectasia", "vascular redness", "sensitive skin", "reactive skin", "erythema", "red cheeks", "red nose", "facial redness", "chronic redness", "diffuse redness", "couperose", "red blotches", "red patches", "skin sensitivity", "inflammatory redness"],
  },
  {
    title: "Skin Laxity & Sagging",
    description: "Tighten loose, crepey, or sagging skin without surgery",
    route: "/skin-laxity",
    category: "Condition",
    keywords: ["skin laxity", "sagging skin", "loose skin", "crepey skin", "jowls", "drooping cheeks", "droopy eyelids", "sagging jawline", "neck sagging", "turkey neck", "skin tightening", "lifting", "non-surgical facelift", "aging face", "loss of firmness", "skin elasticity", "collagen loss", "skin looseness", "flabby skin", "skin drooping", "face drooping", "saggy"],
  },
  {
    title: "Facial Volume Loss",
    description: "Restore sunken cheeks, temples, and hollow facial areas",
    route: "/facial-volume",
    category: "Condition",
    keywords: ["volume loss", "hollow cheeks", "sunken cheeks", "flat cheeks", "hollow temples", "sunken temples", "facial aging", "mid-face volume", "gaunt face", "skeletal appearance", "deflated face", "lost facial fat", "facial wasting", "thin face", "bony face", "cheek augmentation", "cheekbones", "facial contouring", "facial rejuvenation", "youthful volume", "restore volume"],
  },
  {
    title: "Stretch Marks",
    description: "Reduce stretch marks on abdomen, thighs, and arms",
    route: "/stretch-mark",
    category: "Condition",
    keywords: ["stretch marks", "striae", "pregnancy stretch marks", "post pregnancy marks", "weight loss stretch marks", "red stretch marks", "purple stretch marks", "white stretch marks", "silver stretch marks", "stretch marks on stomach", "stretch marks on thighs", "stretch marks on arms", "stretch marks on hips", "stretch marks on breasts", "stretch marks on buttocks", "stretch mark removal", "stretch mark treatment"],
  },
  {
    title: "Stubborn Fat & Body Contouring",
    description: "Non-surgical fat reduction for belly, flanks, and thighs",
    route: "/stubborn-body-fat",
    category: "Condition",
    keywords: ["stubborn fat", "belly fat", "love handles", "muffin top", "bra fat", "back fat", "inner thigh fat", "outer thigh fat", "saddle bags", "double chin", "submental fat", "fat under chin", "arm fat", "bat wings", "flabby arms", "abdominal fat", "visceral fat", "subcutaneous fat", "body fat", "fat pockets", "localized fat", "fat deposits", "fat rolls", "spare tire"],
  },

  {
    title: "XERF Skin Tightening",
    description: "No-needle radiofrequency tightening for the face, jawline, and neck",
    route: "/xerf-skin-tightening",
    category: "Treatment",
    keywords: ["xerf", "xerf skin tightening", "xerf dallas", "structural skin tightening", "radiofrequency skin tightening", "no needle skin tightening", "non-invasive skin tightening", "non-surgical skin tightening", "face tightening", "lower face tightening", "jawline tightening", "neck tightening", "jowl tightening", "skin firming", "loose facial skin", "loose neck skin", "early skin laxity", "collagen stimulation"],
  },
  {
    title: "RF Microneedling + PRP",
    description: "Radiofrequency collagen remodeling for scars, pores, and tightening",
    route: "/rf-microneedling",
    category: "Treatment",
    keywords: ["rf microneedling", "radiofrequency microneedling", "morpheus8", "skin tightening", "collagen remodeling", "acne scar treatment", "pore treatment", "skin texture", "prp microneedling", "prf microneedling", "fractional rf", "micro needling", "needling treatment", "collagen induction", "skin rejuvenation", "face tightening", "neck tightening", "jowl tightening", "scar treatment"],
  },
  {
    title: "Fractional CO2 Laser Resurfacing",
    description: "Deep ablative laser for severe acne scars and wrinkles",
    route: "/fractional-co2",
    category: "Treatment",
    keywords: ["co2 laser", "fractional co2", "ablative laser", "laser resurfacing", "deep acne scars", "severe acne scars", "laser skin renewal", "deep wrinkle treatment", "skin resurfacing", "fractional laser", "carbon dioxide laser", "ablative resurfacing", "laser peel", "deep scar treatment", "laser for scars"],
  },
  {
    title: "DuoVive 1927nm Thulium Laser",
    description: "Non-ablative fractional resurfacing for tone, texture, and pigmentation",
    route: "/duovive",
    category: "Treatment",
    keywords: ["duovive", "thulium laser", "1927nm", "non-ablative laser", "fractional resurfacing", "skin texture treatment", "pigmentation laser", "mild acne scar treatment", "gentle laser", "minimal downtime laser", "skin tone correction", "laser for dark spots", "laser for texture"],
  },
  {
    title: "IPL Photofacial",
    description: "Pulsed light for brown spots, redness, and sun damage",
    route: "/dpl-photofacial",
    category: "Treatment",
    keywords: ["dpl", "photofacial", "ipl", "intense pulsed light", "brown spot treatment", "redness treatment", "sun damage treatment", "vascular treatment", "broken capillaries treatment", "rosacea laser", "sunspot removal", "age spot removal", "freckle removal", "skin brightening light treatment", "near-infrared", "broadband light", "bbl"],
  },
  {
    title: "PicoLaser & Tattoo Removal",
    description: "Picosecond laser for pigmentation, dark spots, and tattoo removal",
    route: "/pico-laser",
    category: "Treatment",
    keywords: ["picolaser", "pico laser", "picosecond laser", "picoway", "enlighten", "pigmentation removal", "dark spot laser", "melasma treatment", "sunspot laser", "tattoo removal", "tattoo removal laser", "remove tattoo", "tattoo fading", "laser tattoo removal", "unwanted tattoo", "old tattoo", "tattoo regret", "tattoo lightening", "black ink tattoo", "colored tattoo removal", "tattoo laser", "pih treatment", "skin brightening laser", "laser for melasma", "laser for dark spots", "laser for pigmentation", "ultra-short pulse laser"],
  },
  {
    title: "AquaFacial",
    description: "Deep cleanse, exfoliate, and hydrate in one relaxing treatment",
    route: "/aquafacial",
    category: "Treatment",
    keywords: ["aquafacial", "aqua facial", "hydrodermabrasion", "deep cleansing facial", "hydration facial", "glow facial", "pore cleansing", "blackhead extraction", "skin hydration", "brightening facial", "vortex facial", "wet exfoliation", "serum infusion", "facial treatment", "luxury facial", "relaxing facial", "skin glow", "dewy skin"],
  },
  {
    title: "HIFU Ultrasound Lifting",
    description: "Non-surgical brow lift, face lift, and neck tightening",
    route: "/hifu",
    category: "Treatment",
    keywords: ["hifu", "ultherapy", "high intensity focused ultrasound", "non-surgical facelift", "ultrasound lifting", "brow lift", "face lift without surgery", "neck tightening", "jawline tightening", "non-invasive lifting", "skin lifting", "deep tissue tightening", "collagen stimulation", "ultrasound skin tightening"],
  },
  {
    title: "Skin Analysis & Consultation",
    description: "Comprehensive skin assessment and personalized treatment plan",
    route: "/skin-analysis",
    category: "Treatment",
    keywords: ["skin analysis", "skin consultation", "skin assessment", "skin type test", "what treatment do i need", "skin evaluation", "personalized skin plan", "skin health check"],
  },
  {
    title: "Botox & Dysport (Neuromodulators)",
    description: "Relax expression lines and prevent wrinkles",
    route: "/neuromodulators",
    category: "Treatment",
    keywords: ["botox", "dysport", "xeomin", "jeuveau", "neuromodulator", "wrinkle relaxer", "wrinkle injection", "anti-wrinkle injection", "forehead botox", "crows feet botox", "frown line botox", "11 lines botox", "glabellar botox", "brow lift botox", "lip flip", "gummy smile", "jaw slimming", "masseter botox", "teeth grinding botox", "bruxism", "hyperhidrosis", "excessive sweating", "neck botox", "platysmal bands", "preventative botox", "baby botox"],
  },
  {
    title: "Dermal Fillers",
    description: "Restore volume and contour with hyaluronic acid fillers",
    route: "/dermal-fillers",
    category: "Treatment",
    keywords: ["dermal fillers", "filler", "hyaluronic acid filler", "juvederm", "restylane", "voluma", "vollure", "volbella", "cheek filler", "cheekbone filler", "nasolabial fold filler", "marionette line filler", "jawline filler", "chin filler", "temple filler", "under eye filler", "tear trough filler", "hand filler", "nose filler", "non-surgical rhinoplasty", "liquid facelift", "facial contouring", "volume restoration"],
  },
  {
    title: "Lip Fillers",
    description: "Natural lip augmentation and definition",
    route: "/lip-fillers",
    category: "Treatment",
    keywords: ["lip filler", "lip augmentation", "lip enhancement", "bigger lips", "fuller lips", "lip plumping", "lip volume", "lip shape", "lip definition", "thin lips", "asymmetric lips", "lip lines", "perioral lines", "juvederm lips", "restylane lips", "natural lip filler", "russian lip technique", "lip injection"],
  },
  {
    title: "Kybella (Double Chin)",
    description: "Dissolve submental fat under the chin without surgery",
    route: "/kybella",
    category: "Treatment",
    keywords: ["kybella", "double chin", "chin fat", "submental fat", "fat under chin", "chin reduction", "neck fat", "deoxycholic acid", "chin contouring", "jawline definition", "chin slimming", "neck contouring", "double chin injection", "dissolve chin fat"],
  },
  {
    title: "PDO Thread Lift",
    description: "Non-surgical lifting with absorbable PDO threads",
    route: "/pdo-threads",
    category: "Treatment",
    keywords: ["pdo threads", "thread lift", "pdo thread lift", "non-surgical facelift", "face lifting threads", "jowl lift", "brow thread lift", "neck thread lift", "cheek lift", "absorbable threads", "collagen threads", "barbed threads", "smooth threads", "thread rejuvenation", "instant lift"],
  },
  {
    title: "Sculptra & Radiesse (Biostimulators)",
    description: "Long-lasting collagen stimulation for natural volume",
    route: "/biostimulators",
    category: "Treatment",
    keywords: ["sculptra", "radiesse", "biostimulator", "collagen stimulator", "poly-l-lactic acid", "plla", "calcium hydroxylapatite", "long-lasting filler", "natural volume", "collagen booster", "anti-aging injection", "face rejuvenation", "skin firming injection", "gradual volume", "natural results filler"],
  },
  {
    title: "Chemical Peels",
    description: "Controlled exfoliation for tone, texture, acne, and pigmentation",
    route: "/chemical-peels",
    category: "Treatment",
    keywords: ["chemical peel", "vi peel", "glycolic peel", "salicylic peel", "lactic acid peel", "tca peel", "jessner peel", "mandelic peel", "skin peel", "exfoliation treatment", "resurfacing peel", "acne peel", "pigmentation peel", "brightening peel", "anti-aging peel", "skin renewal", "peeling treatment"],
  },
  {
    title: "RenaSculpt (HIFEM Muscle Sculpting)",
    description: "Build muscle and burn fat with high-intensity electromagnetic energy",
    route: "/renasculpt",
    category: "Treatment",
    keywords: ["renasculpt", "emsculpt", "hifem", "muscle sculpting", "body sculpting", "abs treatment", "six pack", "core sculpting", "buttock lift", "non-surgical butt lift", "muscle building", "fat burning", "body contouring", "electromagnetic muscle stimulation", "toned body", "defined abs", "glute sculpting"],
  },
  {
    title: "Cryolipolysis (Fat Freezing)",
    description: "Freeze targeted fat cells for long-lasting contour improvement",
    route: "/cryolipolysis",
    category: "Treatment",
    keywords: ["cryolipolysis", "coolsculpting", "fat freezing", "fat reduction", "freeze fat", "love handles treatment", "belly fat treatment", "non-surgical liposuction", "fat elimination", "cold fat reduction", "bra fat treatment", "inner thigh fat treatment", "arm fat treatment", "fat cells", "long-lasting fat reduction"],
  },
  {
    title: "1060nm Diode Laser Lipolysis",
    description: "Laser-based fat reduction and body contouring",
    route: "/diode-laser-lipolysis",
    category: "Treatment",
    keywords: ["diode laser lipolysis", "laser lipolysis", "1060nm laser", "laser fat reduction", "laser body contouring", "laser fat melting", "non-invasive fat removal", "laser slimming"],
  },
  {
    title: "PRP Hair Restoration",
    description: "Platelet-rich plasma injections to regrow thinning hair",
    route: "/hair-growth",
    category: "Treatment",
    keywords: ["hair loss", "hair thinning", "thinning hair", "hair restoration", "prp hair", "prf hair", "platelet hair treatment", "hair regrowth", "androgenetic alopecia", "female hair loss", "male pattern baldness", "alopecia", "receding hairline", "hair shedding", "hair fall", "scalp treatment", "hair growth treatment", "hair density"],
  },
  {
    title: "LLLT Laser Hair Restoration",
    description: "Low-level laser therapy to stimulate hair follicles",
    route: "/lllt-hair-restoration",
    category: "Treatment",
    keywords: ["lllt", "low level laser therapy", "laser cap", "laser helmet", "laser hair growth", "laser for hair loss", "photobiomodulation hair", "hair follicle stimulation", "hair regrowth laser"],
  },
  {
    title: "Laser Hair Removal (AresLite)",
    description: "Long-lasting hair reduction for all skin types",
    route: "/areslite-hair-removal",
    category: "Treatment",
    keywords: ["laser hair removal", "hair removal", "long-lasting hair reduction", "unwanted hair", "facial hair removal", "leg hair removal", "bikini hair removal", "underarm hair removal", "back hair removal", "chest hair removal", "brazilian laser", "full body laser", "smooth skin", "hair-free skin", "shaving alternative", "waxing alternative", "areslite"],
  },
  {
    title: "Vaginal Rejuvenation",
    description: "Non-surgical vaginal tightening and rejuvenation",
    route: "/vaginal-rejuvenation",
    category: "Treatment",
    keywords: ["vaginal rejuvenation", "vaginal tightening", "vaginal laxity", "intimate rejuvenation", "feminine rejuvenation", "postpartum", "post pregnancy vaginal", "vaginal dryness", "urinary incontinence", "stress incontinence", "pelvic health"],
  },
  {
    title: "O-Shot & P-Shot (PRP Sexual Wellness)",
    description: "PRP injections for sexual enhancement and wellness",
    route: "/prp-sexual-wellness",
    category: "Treatment",
    keywords: ["o-shot", "p-shot", "prp sexual wellness", "sexual enhancement", "orgasm shot", "priapus shot", "sexual function", "libido", "sexual wellness", "intimate prp"],
  },
  {
    title: "Pelvic Floor Strengthening (HIFEM)",
    description: "Strengthen pelvic floor muscles with electromagnetic energy",
    route: "/pelvic-floor",
    category: "Treatment",
    keywords: ["pelvic floor", "pelvic floor strengthening", "incontinence", "bladder leakage", "urinary leakage", "postpartum recovery", "pelvic health", "emsella", "hifem pelvic", "kegel alternative"],
  },
  {
    title: "Shockwave Therapy & Regenerative Medicine",
    description: "Non-surgical pain relief and tissue regeneration",
    route: "/pain-regenerative-medicine",
    category: "Treatment",
    keywords: ["shockwave therapy", "eswt", "extracorporeal shockwave", "pain treatment", "joint pain", "tendon pain", "plantar fasciitis", "tennis elbow", "shoulder pain", "knee pain", "chronic pain", "regenerative medicine", "prp joint injection", "tissue regeneration", "sports injury", "musculoskeletal"],
  },
  {
    title: "Medical Weight Loss",
    description: "Physician-supervised weight loss with GLP-1 medications",
    route: "/weight-loss",
    category: "Treatment",
    keywords: ["weight loss", "medical weight loss", "glp-1", "glp1", "glp1", "weight loss injection", "lose weight", "obesity treatment", "bmi", "physician weight loss", "supervised weight loss", "appetite suppression", "metabolic health"],
  },
  {
    title: "Hormone Optimization",
    description: "Bioidentical hormone replacement for men and women",
    route: "/hormone-optimization",
    category: "Treatment",
    keywords: ["hormone therapy", "bhrt", "bioidentical hormones", "testosterone replacement", "estrogen therapy", "progesterone", "menopause treatment", "perimenopause", "andropause", "low testosterone", "low t", "hormone imbalance", "fatigue treatment", "low energy hormones", "mood hormones", "hormone optimization", "hrt"],
  },
  {
    title: "Peptide Therapy",
    description: "Targeted peptides for anti-aging, recovery, and performance",
    route: "/peptide-therapy",
    category: "Treatment",
    keywords: ["peptide therapy", "peptides", "bpc-157", "tb-500", "growth hormone peptides", "ipamorelin", "cjc-1295", "anti-aging peptides", "recovery peptides", "performance peptides", "sermorelin", "skin peptides"],
  },
  {
    title: "Functional Lab Testing",
    description: "Comprehensive labs to uncover root causes of health concerns",
    route: "/functional-lab-testing",
    category: "Treatment",
    keywords: ["lab testing", "functional labs", "blood work", "hormone panel", "thyroid testing", "nutrient deficiency", "metabolic panel", "comprehensive labs", "health testing", "root cause"],
  },
  {
    title: "AI Skin Analysis",
    description: "Free AI-powered skin assessment with personalized treatment plan",
    route: "/ai-skin-analysis",
    category: "Info",
    keywords: ["ai skin analysis", "skin analysis", "skin assessment", "free skin analysis", "ai skin", "personalized treatment plan", "skin report", "skin score", "what is wrong with my skin", "skin concerns analysis", "skin type", "fitzpatrick", "skin health", "analyze my skin", "skin quiz", "what treatment do i need", "skin recommendation"],
  },
  {
    title: "Technology Comparison",
    description: "Compare our advanced devices and find the right treatment",
    route: "/technology-comparison",
    category: "Technology",
    keywords: ["technology", "devices", "equipment", "laser comparison", "which treatment", "compare treatments", "best treatment for me", "treatment comparison", "device comparison", "our machines", "rf vs laser", "laser vs filler", "treatment options"],
  },
  {
    title: "Face & Skin Rejuvenation",
    description: "All facial treatments - lasers, energy devices, and skin renewal",
    route: "/face-skin-rejuvenation",
    category: "Service",
    keywords: ["face treatments", "facial rejuvenation", "skin rejuvenation", "all face treatments", "skin treatments", "facial services"],
  },
  {
    title: "Injectables Overview",
    description: "Botox, fillers, threads, and biostimulators",
    route: "/injectables",
    category: "Service",
    keywords: ["injectables", "injections", "all injectables", "filler and botox", "injectable treatments", "needle treatments"],
  },
  {
    title: "Body Contouring Overview",
    description: "Non-surgical fat reduction, muscle sculpting, and body shaping",
    route: "/body-contouring",
    category: "Service",
    keywords: ["body contouring", "body sculpting", "body shaping", "all body treatments", "non-surgical body", "slimming"],
  },
  {
    title: "Intimate Wellness Overview",
    description: "Vaginal rejuvenation, sexual wellness, and pelvic health",
    route: "/intimate-wellness",
    category: "Service",
    keywords: ["intimate wellness", "womens wellness", "sexual health", "pelvic health", "feminine health"],
  },
  {
    title: "Skin Conditions Overview",
    description: "Browse all skin conditions we treat",
    route: "/face-skin-conditions",
    category: "Condition",
    keywords: ["skin conditions", "all conditions", "skin problems", "what conditions do you treat", "skin issues"],
  },
  {
    title: "Wellness & Anti-Aging",
    description: "Holistic wellness services for longevity and vitality",
    route: "/wellness",
    category: "Service",
    keywords: ["wellness", "anti-aging", "longevity", "vitality", "holistic health", "iv therapy", "vitamin drip", "energy boost", "immune support", "wellness services"],
  },
  {
    title: "Membership Plans",
    description: "Monthly memberships for ongoing skin health and savings",
    route: "/membership",
    category: "Service",
    keywords: ["membership", "monthly plan", "subscription", "savings plan", "maintenance plan", "loyalty program", "monthly treatments", "ongoing care", "treatment package", "monthly membership", "skin membership", "med spa membership"],
  },
];
